import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CreateAssetDto } from '@thehubz/client/data-access-api';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AnnotationsPdfService } from '../../services/annotations-pdf.service';
import { AssetAnnotatorService } from '../../services/asset-annotator.service';
import { TmpAssetStorageService } from '../../services/tmp-asset-storage.service';

@Component({
  selector: 'thehubz-pdf-editor',
  templateUrl: './pdf-editor.component.html',
  styleUrls: ['./pdf-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, PdfViewerModule, MatIconModule],
})
export class PdfEditorComponent implements OnInit {
  @Input() fileUrl = '';
  private sceneWidth = 0;
  private sceneHeight = 0;
  private containerWidth = 0;
  private containerHeight = 0;
  private assetTypes = CreateAssetDto.AssetTypeEnum;
  public zoom = 1;
  public minZoom = 0.25;
  public maxZoom = 2;
  public zoomStep = 0.25;

  @Output() openFullScreenClick = new EventEmitter<void>();
  totalPdfPages = 0;
  isPdfLoaded = false;
  pdfCurrentPage!: number;
  isPrevNextDisabled = false;

  asset: any;
  lastPageCommented = -1;
  allowPdfLoad = true;

  constructor(
    private anService: AssetAnnotatorService,
    private tmpAssetStorageService: TmpAssetStorageService,
    private annotationsPdfService: AnnotationsPdfService,
  ) {}

  ngOnInit(): void {
    this.tmpAssetStorageService.getAsset().subscribe((asset: any) => {
      if (Object.keys(asset).length > 0 && JSON.stringify(this.asset) !== JSON.stringify(asset)) {
        this.allowPdfLoad = false;
        this.asset = asset;
        this.getLastPage();
      }
    });
  }

  afterPdfLoadComplete(pdfData: any) {
    console.log('%cAfter Pdf complete', 'color:blue');
    this.totalPdfPages = pdfData.numPages;
    this.isPdfLoaded = true;
    //hiding the internal scrollbar of pdf viewer
    document.getElementsByClassName('ng2-pdf-viewer-container')[0].classList.add('hide-overflow');
    setTimeout(() => {
      this.annotationsPdfService.updateCurrentPage(this.pdfCurrentPage);
      this.sceneHeight = document.getElementsByClassName('page')[0].clientHeight;
      this.sceneWidth = document.getElementsByClassName('page')[0].clientWidth;
      this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Pdf, this.pdfCurrentPage, 0);

      const annotationWindow = document.getElementById('annotation-window');
      this.containerWidth = annotationWindow?.clientWidth || 0;
      this.containerHeight = annotationWindow?.clientHeight || 0;

      if (annotationWindow) {
        //fixing the frame so that overflow works and this is necessary for PDF asset
        annotationWindow.style.width = annotationWindow.clientWidth + 12 + 'px';
      }
    }, 500);
  }

  pdfPageRendered(event: any) {
    setTimeout(() => {
      const pdfContainer = document.getElementById('pdf-container');
      if (pdfContainer) {
        const pdfPage = document.getElementsByClassName('page')[0];
        if (this.containerWidth) {
          if (pdfPage.clientWidth > this.containerWidth) pdfContainer.style.width = pdfPage.clientWidth + 'px';
          else pdfContainer.style.width = this.containerWidth + 'px';
        }
        pdfContainer.style.height = pdfPage.clientHeight + 'px';
      }
    }, 600);
  }

  draw(tool: string) {
    const scrollLeft = document.getElementById('annotation-window')?.scrollLeft || 0;
    const scrollTop = document.getElementById('annotation-window')?.scrollTop || 0;
    this.anService.drawTool(tool, this.assetTypes.Pdf, scrollLeft, scrollTop);
  }

  getLastPage() {
    if (
      typeof this.asset !== 'undefined' &&
      typeof this.asset.currentVersion !== 'undefined' &&
      typeof this.asset.currentVersion.annotations !== 'undefined' &&
      typeof this.asset.currentVersion.annotations.children !== 'undefined' &&
      Array.isArray(this.asset.currentVersion.annotations.children) &&
      this.asset.currentVersion.annotations.children.length > 0 &&
      typeof this.asset.currentVersion.annotations.children[0].children !== 'undefined'
    ) {
      const commentsWithPages = this.asset.currentVersion.annotations.children[0].children.filter(
        (r: any) => typeof r.attrs !== 'undefined' && typeof r.attrs.page !== 'undefined',
      );
      const totalComments = commentsWithPages.length;
      const lastPage: number = commentsWithPages[totalComments - 1].attrs.page as number;
      this.pdfCurrentPage = lastPage;
      this.gotoPage(lastPage);
    } else {
      this.pdfCurrentPage = 1;
      this.gotoPage(1);
    }
  }

  gotoPage(pageNum: number) {
    this.pdfCurrentPage = pageNum;
    this.annotationsPdfService.updateCurrentPage(this.pdfCurrentPage);
    setTimeout(() => {
      this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Pdf, this.pdfCurrentPage, 0);
      this.allowPdfLoad = true;
    }, 1000);
  }

  nextPage() {
    this.anService.destroyStage();
    this.pdfCurrentPage += 1;
    this.annotationsPdfService.updateCurrentPage(this.pdfCurrentPage);
    this.isPrevNextDisabled = true;
    setTimeout(() => {
      this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Pdf, this.pdfCurrentPage, 0);
      this.isPrevNextDisabled = false;
    }, 1000);
  }

  previousPage() {
    this.anService.destroyStage();
    this.pdfCurrentPage -= 1;
    this.annotationsPdfService.updateCurrentPage(this.pdfCurrentPage);
    this.isPrevNextDisabled = true;
    setTimeout(() => {
      this.anService.initKonvaStage('konva-holder', this.sceneWidth, this.sceneHeight, this.assetTypes.Pdf, this.pdfCurrentPage, 0);
      this.isPrevNextDisabled = false;
    }, 1000);
  }

  async zoomPDF(direction: number) {
    return new Promise<void>((resolve) => {
      this.zoomAsset(direction);
      setTimeout(() => {
        const pdfPage = document.getElementsByClassName('page')[0];
        this.sceneHeight = pdfPage.clientHeight;
        this.sceneWidth = pdfPage.clientWidth;
        this.anService.resizeKovnvaCanvas(pdfPage.clientWidth, pdfPage.clientHeight, true);

        const pdfContainer = document.querySelector('#pdf-container');
        const konvaContainer = document.querySelector('.konvajs-content') as HTMLElement;

        if (pdfContainer && konvaContainer && pdfContainer.clientWidth && pdfContainer.clientHeight) {
          konvaContainer.style.width = String(pdfContainer.clientWidth);
          konvaContainer.style.height = String(pdfContainer.clientHeight);
        }

        resolve();
      }, 10);
    });
  }

  zoomAsset(direction: number) {
    if (direction == 1 && this.zoom < this.maxZoom) this.zoom += this.zoomStep;
    if (direction == -1 && this.zoom > this.minZoom) this.zoom -= this.zoomStep;
  }

  async resetZoom() {
    this.zoom = 1.25;
    await this.zoomPDF(-1);
  }

  scrollHandler(e: any) {
    document.getElementById('konva-holder')?.scrollTo({ top: e.target.scrollTop, left: e.target.scrollLeft });
  }

  openAssetFullscreen() {
    this.openFullScreenClick.emit();
  }
}
